import React, { useState, useEffect, useCallback } from 'react';
import { contentAPI } from '../api/content';

/**
 * Rejected Content review box for a campaign. Shows content that was NOT added
 * because its author isn't on the campaign roster (so no stray creator was
 * created). Each row can be manually rejected (dismissed) — nothing is deleted,
 * and dismissed rows stay viewable via "Show dismissed". Styled to match the
 * dark campaign cards. Auto-hides when there's nothing to show.
 */
const platformDot = {
  youtube: '#ef4444', twitch: '#a970ff', kick: '#00e70a', tiktok: '#e5e7eb',
  instagram: '#ec4899', twitter: '#38bdf8',
};

const RejectedContentBox = ({ campaignId }) => {
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [closingId, setClosingId] = useState(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await contentAPI.getRejected({ campaignId, status: showClosed ? 'all' : 'open' });
      setRows(res.data?.rejected || []);
      setOpenCount(res.data?.openCount || 0);
    } catch (e) {
      /* silent — box just stays empty */
    } finally {
      setLoading(false);
    }
  }, [campaignId, showClosed]);

  useEffect(() => { load(); }, [load]);

  const handleReject = async (id) => {
    setClosingId(id);
    try {
      await contentAPI.closeRejected(id);
      if (showClosed) {
        setRows(rows.map(r => r.id === id ? { ...r, status: 'closed', closedAt: new Date().toISOString() } : r));
      } else {
        setRows(rows.filter(r => r.id !== id));
      }
      setOpenCount(c => Math.max(0, c - 1));
    } catch (e) {
      /* leave row in place on failure */
    } finally {
      setClosingId(null);
    }
  };

  // Hide entirely when there's nothing open and history isn't being viewed.
  // NOTE: no `!loading` guard here — during the initial load openCount is still 0,
  // and rendering the box mid-load made it flash up for a frame on every refresh
  // before the fetch resolved to "nothing open". Stay hidden until we KNOW there's
  // something to show.
  if (openCount === 0 && !showClosed) return null;

  return (
    <div className="bg-[#241d33] border border-white rounded-xl mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 font-semibold text-white text-sm">
          🚫 Rejected Content
          {openCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.3rem] h-5 px-1.5 text-[11px] font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 rounded-full">
              {openCount}
            </span>
          )}
        </span>
        <span className="text-white/40 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <p className="text-[11px] text-white mb-3 leading-relaxed">
            Content that couldn't be attached because the creator isn't on this campaign. No creator was created.
            To add any of these for real, add the creator via <span className="text-white font-semibold">Add Creators</span>, then re-add the content.
          </p>

          {loading ? (
            <p className="text-xs text-white py-4 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-xs text-white py-4 text-center">Nothing rejected. 🎉</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${r.status === 'closed' ? 'bg-white/3 border-white/10 opacity-60' : 'bg-white/5 border-white/10'}`}
                >
                  {r.thumbnailUrl ? (
                    <img src={r.thumbnailUrl} alt="" className="w-14 h-14 rounded-md object-cover flex-shrink-0 ring-1 ring-white/10" />
                  ) : (
                    <div className="w-14 h-14 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0 text-white/40 text-lg">🎬</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: platformDot[r.platform] || '#9ca3af' }} />
                      <span className="text-sm font-medium text-white truncate">
                        {r.authorName || r.authorHandle || 'Unknown author'}
                      </span>
                      {r.status === 'closed' && <span className="text-[10px] uppercase tracking-wide text-white/80">dismissed</span>}
                    </div>
                    <p className="text-[11px] text-white mb-1">{r.reason}</p>
                    <a href={r.contentUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-sc-pink hover:underline break-all">
                      {r.title ? r.title : r.contentUrl}
                    </a>
                    <p className="text-[10px] text-white/80 mt-0.5">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString('en-GB') : ''}
                    </p>
                  </div>
                  {r.status !== 'closed' && (
                    <button
                      onClick={() => handleReject(r.id)}
                      disabled={closingId === r.id}
                      className="flex-shrink-0 rounded-full px-3.5 py-1 text-[11px] font-semibold text-white bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-sc-pink hover:from-pink-500/30 hover:to-purple-500/30 disabled:opacity-50"
                    >
                      {closingId === r.id ? '…' : 'Reject'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex justify-end">
            <label className="flex items-center gap-1.5 text-[11px] text-white cursor-pointer">
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              Show dismissed
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default RejectedContentBox;
